<template>
  <div class="jiangxi_map">
    <div ref="chart_container" class="jiangxi_map__chart"></div>

    <div
      v-if="display_mode === 'farmland' || display_mode === 'ndvi'"
      class="jiangxi_map__year_switcher"
      :class="{ 'jiangxi_map__year_switcher--construction': display_mode === 'ndvi' }"
    >
      <button
        v-for="year_value in active_year_options"
        :key="year_value"
        type="button"
        class="jiangxi_map__year_button"
        :class="{
          'jiangxi_map__year_button--active': active_monitor_year === year_value,
          'jiangxi_map__year_button--construction': display_mode === 'ndvi'
        }"
        @click="update_monitor_year(year_value)"
      >
        {{ year_value }}
      </button>
    </div>

    <div
      v-if="thematic_legend"
      class="jiangxi_map__legend"
      :class="{ 'jiangxi_map__legend--construction': display_mode === 'ndvi' }"
    >
      <div class="jiangxi_map__legend_header">
        <strong>{{ thematic_legend.title }}</strong>
        <span>{{ thematic_legend.year }} 年</span>
      </div>
      <div class="jiangxi_map__legend_body">
        <span class="jiangxi_map__legend_level">高</span>
        <div
          class="jiangxi_map__legend_bar"
          :style="{ background: thematic_legend.gradient }"
        ></div>
        <span class="jiangxi_map__legend_level">低</span>
      </div>
      <div class="jiangxi_map__legend_scale">
        <span>{{ thematic_legend.maxLabel }}</span>
        <span>{{ thematic_legend.minLabel }}</span>
      </div>
    </div>

    <div v-if="display_mode === 'base'" class="jiangxi_map__title">
      <strong>赣州市</strong>
      <span>GANZHOU</span>
    </div>

    <div class="jiangxi_map__hud">
      <div class="jiangxi_map__summary">
        <span>当前区域</span>
        <strong>{{ selected_region_label }}</strong>
      </div>
      <button type="button" class="jiangxi_map__action" @click="reset_map_view">
        返回全图
      </button>
    </div>
  </div>
</template>

<script>
import * as echarts from '../echats.js';
import city_geo_json from '../../map/Ganzhou-GeoJSON.json';

const DISTRICT_MAP_NAME = 'ganzhou-district-map';
const DEFAULT_MAP_ZOOM = 1;
const DEFAULT_CITY_NAME = '赣州市';

const FARMLAND_LAYER_MAP = {
  2020: { file_name: '2020年耕地分布填充图层.geojson', field_name: 'crop_2020', label: '耕地变化值' },
  2021: { file_name: '2021年耕地分布填充图层.geojson', field_name: 'crop_2021', label: '耕地变化值' },
  2022: { file_name: '2022年耕地分布填充图层.geojson', field_name: 'crop_2022', label: '耕地变化值' },
  2023: { file_name: '2023年耕地分布填充图层.geojson', field_name: 'crop_2023', label: '耕地变化值' },
  2024: { file_name: '2024年耕地分布填充图层.geojson', field_name: 'crop_2024', label: '耕地变化值' }
};

const CONSTRUCTION_LAYER_MAP = {
  2021: { file_name: '2021年建设用地分布填充图层.geojson', field_name: 'build_2021', label: '建设用地值' },
  2022: { file_name: '2022年建设用地分布填充图层.geojson', field_name: 'build_2022', label: '建设用地值' },
  2023: { file_name: '2023年建设用地分布填充图层.geojson', field_name: 'build_2023', label: '建设用地值' },
  2024: { file_name: '2024年建设用地分布填充图层.geojson', field_name: 'build_2024', label: '建设用地值' }
};

const DISTRICT_METRICS = {
  章贡区: { value: 86, level: 3 },
  南康区: { value: 75, level: 2 },
  赣县区: { value: 70, level: 2 },
  信丰县: { value: 67, level: 2 },
  大余县: { value: 52, level: 1 },
  上犹县: { value: 58, level: 1 },
  崇义县: { value: 49, level: 1 },
  安远县: { value: 55, level: 1 },
  定南县: { value: 57, level: 1 },
  全南县: { value: 54, level: 1 },
  宁都县: { value: 72, level: 2 },
  于都县: { value: 76, level: 2 },
  兴国县: { value: 73, level: 2 },
  会昌县: { value: 60, level: 1 },
  寻乌县: { value: 53, level: 1 },
  石城县: { value: 56, level: 1 },
  瑞金市: { value: 80, level: 3 },
  龙南市: { value: 78, level: 3 }
};

export default {
  name: 'JiangxiMap',
  props: {
    display_mode: {
      type: String,
      default: 'base'
    },
    county_profiles: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['region-click'],
  data() {
    return {
      chart_instance: null,
      resize_observer: null,
      selected_region_name: DEFAULT_CITY_NAME,
      geo_center: null,
      geo_zoom: DEFAULT_MAP_ZOOM,
      region_records: [],
      farmland_monitor_year: 2024,
      construction_monitor_year: 2024,
      farmland_layer_geo_json_map: {},
      construction_layer_geo_json_map: {}
    };
  },
  computed: {
    active_year_options() {
      if (this.display_mode === 'farmland') {
        return [2020, 2021, 2022, 2023, 2024];
      }

      if (this.display_mode === 'ndvi') {
        return [2021, 2022, 2023, 2024];
      }

      return [];
    },
    active_monitor_year() {
      return this.display_mode === 'ndvi'
        ? this.construction_monitor_year
        : this.farmland_monitor_year;
    },
    thematic_legend() {
      if (this.display_mode !== 'farmland' && this.display_mode !== 'ndvi') {
        return null;
      }

      const thematic_records = this.build_thematic_records(this.display_mode);
      if (!thematic_records.length) {
        return null;
      }

      const numeric_values = thematic_records
        .map((record) => Number(record.thematic_value))
        .filter((value) => Number.isFinite(value));

      if (!numeric_values.length) {
        return null;
      }

      const is_farmland = this.display_mode === 'farmland';
      const layer_config = this.get_layer_config(this.display_mode);

      return {
        title: layer_config?.label || '专题值',
        year: this.active_monitor_year,
        minLabel: this.format_metric(Math.min(...numeric_values), 0),
        maxLabel: this.format_metric(Math.max(...numeric_values), 0),
        gradient: is_farmland
          ? 'linear-gradient(180deg, rgba(223, 246, 127, 0.98) 0%, rgba(112, 198, 88, 0.96) 30%, rgba(50, 145, 71, 0.95) 60%, rgba(28, 90, 57, 0.94) 80%, rgba(15, 46, 38, 0.94) 100%)'
          : 'linear-gradient(180deg, rgba(255, 202, 96, 0.98) 0%, rgba(238, 115, 34, 0.96) 35%, rgba(185, 74, 24, 0.95) 60%, rgba(121, 47, 18, 0.94) 80%, rgba(61, 26, 14, 0.94) 100%)'
      };
    },
    selected_region_label() {
      return this.selected_region_name;
    }
  },
  watch: {
    display_mode() {
      this.render_map();
    },
    farmland_monitor_year() {
      if (this.display_mode === 'farmland') {
        this.render_map();
      }
    },
    construction_monitor_year() {
      if (this.display_mode === 'ndvi') {
        this.render_map();
      }
    },
    county_profiles: {
      deep: true,
      handler() {
        this.render_map();
      }
    }
  },
  mounted() {
    this.initialize_chart();
    this.load_map().catch((error) => {
      console.error('Failed to initialize map data:', error);
      this.render_empty_state();
    });
    window.addEventListener('resize', this.handle_resize);

    if ('ResizeObserver' in window && this.$refs.chart_container) {
      this.resize_observer = new window.ResizeObserver(() => {
        this.handle_resize();
      });
      this.resize_observer.observe(this.$refs.chart_container);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    this.resize_observer?.disconnect();
    this.chart_instance?.dispose();
    this.chart_instance = null;
  },
  methods: {
    normalize_region_name(region_name) {
      return typeof region_name === 'string' ? region_name.trim() : '';
    },

    get_county_profile(region_name) {
      const normalized_region_name = this.normalize_region_name(region_name);
      return this.county_profiles?.[normalized_region_name] || null;
    },

    format_metric(metric_value, fraction_digits = 2, unit = '') {
      const numeric_value = Number(metric_value);
      if (!Number.isFinite(numeric_value)) {
        return '--';
      }

      return `${numeric_value.toFixed(fraction_digits)}${unit}`;
    },

    resolve_level_label(level_value) {
      const level_label_map = {
        1: '低风险',
        2: '中风险',
        3: '高风险'
      };

      return level_label_map[level_value] || '待评估';
    },

    build_common_tooltip_lines(region_name) {
      const county_profile = this.get_county_profile(region_name);
      if (!county_profile) {
        return [];
      }

      return [
        `综合风险：${county_profile.risk_label} / ${county_profile.risk_score_label}`,
        `NDVI：${county_profile.ndvi_label}`,
        `生态质量：${county_profile.eco_quality_label}`,
        `协调度：${county_profile.coordination_label}`,
        `水体占比：${this.format_metric(county_profile.share_ratio, 2, '%')}`
      ];
    },

    build_base_tooltip_content(region_name) {
      const region_record = this.find_region_record(region_name);
      const tooltip_lines = [`<strong>${region_name}</strong>`];

      if (region_record) {
        tooltip_lines.push(`监测指数：${region_record.value}`);
        tooltip_lines.push(`风险等级：${this.resolve_level_label(region_record.level)}`);
      }

      const county_tooltip_lines = this.build_common_tooltip_lines(region_name);
      if (county_tooltip_lines.length) {
        tooltip_lines.push(...county_tooltip_lines);
      }

      if (tooltip_lines.length === 1) {
        tooltip_lines.push('当前暂无可用监测数据');
      }

      return tooltip_lines.join('<br/>');
    },

    build_thematic_tooltip_content(region_name, thematic_value, tooltip_title, monitor_year) {
      const tooltip_lines = [`<strong>${region_name}</strong>`];

      if (Number.isFinite(thematic_value)) {
        tooltip_lines.push(`监测年份：${monitor_year}`);
        tooltip_lines.push(`${tooltip_title}：${thematic_value.toLocaleString('zh-CN')}`);
      } else {
        tooltip_lines.push(`${tooltip_title}：暂无数据`);
      }

      const county_tooltip_lines = this.build_common_tooltip_lines(region_name);
      if (county_tooltip_lines.length) {
        tooltip_lines.push(...county_tooltip_lines);
      }

      return tooltip_lines.join('<br/>');
    },

    initialize_chart() {
      if (!this.$refs.chart_container) {
        return;
      }

      this.chart_instance = echarts.init(this.$refs.chart_container);
    },

    async load_map() {
      if (!this.chart_instance) {
        return;
      }

      echarts.registerMap(DISTRICT_MAP_NAME, city_geo_json);
      await this.load_thematic_layers(FARMLAND_LAYER_MAP, 'farmland_layer_geo_json_map');
      await this.load_thematic_layers(CONSTRUCTION_LAYER_MAP, 'construction_layer_geo_json_map');

      this.region_records = city_geo_json.features
        .map((feature) => {
          const { name, center, centroid } = feature.properties || {};
          const metric_record = DISTRICT_METRICS[name] || { value: 50, level: 1 };

          return {
            name,
            coord: centroid || center || null,
            value: metric_record.value,
            level: metric_record.level
          };
        })
        .filter((record) => Array.isArray(record.coord));

      this.render_map();
      this.bind_chart_events();
    },

    async load_thematic_layers(layer_map, target_key) {
      const layer_entries = Object.entries(layer_map);

      const loaded_entries = await Promise.all(layer_entries.map(async ([year_key, layer_config]) => {
        const response = await fetch(`/map/${encodeURIComponent(layer_config.file_name)}`);
        if (!response.ok) {
          throw new Error(`Failed to load thematic layer ${layer_config.file_name}: HTTP ${response.status}`);
        }

        const geo_json = await response.json();
        return [year_key, geo_json];
      }));

      this[target_key] = Object.fromEntries(loaded_entries);
    },

    find_region_record(region_name) {
      return this.region_records.find((record) => record.name === region_name) || null;
    },

    build_geo_regions(region_style_map) {
      return this.region_records.map((record) => {
        const is_selected = record.name === this.selected_region_name;

        return {
          name: record.name,
          itemStyle: is_selected
            ? {
              areaColor: region_style_map.selected.areaColor,
              borderColor: region_style_map.selected.borderColor,
              borderWidth: region_style_map.selected.borderWidth,
              shadowBlur: region_style_map.selected.shadowBlur,
              shadowColor: region_style_map.selected.shadowColor
            }
            : {
              areaColor: region_style_map.normal.areaColor,
              borderColor: region_style_map.normal.borderColor,
              borderWidth: region_style_map.normal.borderWidth,
              shadowBlur: region_style_map.normal.shadowBlur,
              shadowColor: region_style_map.normal.shadowColor
            },
          label: {
            color: is_selected ? '#ffffff' : '#eaf8ff',
            fontWeight: 'bold'
          },
          emphasis: {
            itemStyle: {
              areaColor: region_style_map.emphasis.areaColor,
              borderColor: region_style_map.emphasis.borderColor,
              borderWidth: region_style_map.emphasis.borderWidth,
              shadowBlur: region_style_map.emphasis.shadowBlur,
              shadowColor: region_style_map.emphasis.shadowColor
            },
            label: {
              color: '#ffffff'
            }
          }
        };
      });
    },

    build_scatter_series_data() {
      return this.region_records.map((record) => ({
        name: record.name,
        value: [...record.coord, record.value, record.level]
      }));
    },

    get_layer_config(display_mode) {
      if (display_mode === 'farmland') {
        return FARMLAND_LAYER_MAP[this.farmland_monitor_year] || FARMLAND_LAYER_MAP[2024];
      }

      return CONSTRUCTION_LAYER_MAP[this.construction_monitor_year] || CONSTRUCTION_LAYER_MAP[2024];
    },

    get_layer_geo_json(display_mode) {
      const year_key = String(
        display_mode === 'farmland' ? this.farmland_monitor_year : this.construction_monitor_year
      );

      return display_mode === 'farmland'
        ? this.farmland_layer_geo_json_map[year_key]
        : this.construction_layer_geo_json_map[year_key];
    },

    build_thematic_records(display_mode) {
      const layer_config = this.get_layer_config(display_mode);
      const geo_json = this.get_layer_geo_json(display_mode);

      if (!geo_json || !Array.isArray(geo_json.features)) {
        return [];
      }

      return geo_json.features.map((feature) => {
        const properties = feature.properties || {};
        return {
          name: properties.name,
          coord: properties.centroid || properties.center || null,
          thematic_value: Number(properties[layer_config.field_name] || 0)
        };
      });
    },

    interpolate_color(value, min_value, max_value, color_stops, alpha = 0.9) {
      if (max_value <= min_value) {
        const last_color = color_stops[color_stops.length - 1];
        return `rgba(${last_color[0]}, ${last_color[1]}, ${last_color[2]}, ${alpha})`;
      }

      const ratio = (value - min_value) / (max_value - min_value);
      const scaled_index = ratio * (color_stops.length - 1);
      const lower_index = Math.floor(scaled_index);
      const upper_index = Math.min(color_stops.length - 1, lower_index + 1);
      const mix_ratio = scaled_index - lower_index;
      const lower_color = color_stops[lower_index];
      const upper_color = color_stops[upper_index];

      const mixed_color = lower_color.map((channel_value, index) =>
        Math.round(channel_value + (upper_color[index] - channel_value) * mix_ratio)
      );

      return `rgba(${mixed_color[0]}, ${mixed_color[1]}, ${mixed_color[2]}, ${alpha})`;
    },

    build_farmland_fill_color(value, min_value, max_value) {
      return this.interpolate_color(value, min_value, max_value, [
        [15, 46, 38],
        [28, 90, 57],
        [50, 145, 71],
        [112, 198, 88],
        [223, 246, 127]
      ], 0.94);
    },

    build_construction_fill_color(value, min_value, max_value) {
      return this.interpolate_color(value, min_value, max_value, [
        [61, 26, 14],
        [121, 47, 18],
        [185, 74, 24],
        [238, 115, 34],
        [255, 202, 96]
      ], 0.94);
    },

    build_thematic_regions(display_mode) {
      const thematic_records = this.build_thematic_records(display_mode);
      if (!thematic_records.length) {
        return [];
      }

      const value_list = thematic_records.map((record) => record.thematic_value);
      const min_value = Math.min(...value_list);
      const max_value = Math.max(...value_list);
      const is_farmland = display_mode === 'farmland';

      return thematic_records.map((record) => {
        const is_selected = record.name === this.selected_region_name;
        const area_color = is_farmland
          ? this.build_farmland_fill_color(record.thematic_value, min_value, max_value)
          : this.build_construction_fill_color(record.thematic_value, min_value, max_value);

        return {
          name: record.name,
          itemStyle: {
            areaColor: area_color,
            borderColor: is_farmland
              ? (is_selected ? '#fbffd0' : 'rgba(211, 248, 177, 0.96)')
              : (is_selected ? '#fff0c5' : 'rgba(255, 212, 148, 0.96)'),
            borderWidth: is_selected ? 1.95 : 1.32,
            shadowBlur: is_selected ? 30 : 20,
            shadowColor: is_farmland
              ? (is_selected ? 'rgba(214, 245, 123, 0.36)' : 'rgba(76, 161, 92, 0.24)')
              : (is_selected ? 'rgba(255, 187, 86, 0.38)' : 'rgba(224, 109, 35, 0.24)')
          },
          label: {
            color: is_farmland ? '#f4fde9' : '#fff2e3',
            fontWeight: 'bold'
          },
          emphasis: {
            itemStyle: {
              areaColor: area_color,
              borderColor: is_farmland ? '#ffffdd' : '#fff5d5',
              borderWidth: 1.86,
              shadowBlur: 28,
              shadowColor: is_farmland
                ? 'rgba(220, 246, 139, 0.34)'
                : 'rgba(255, 191, 95, 0.34)'
            },
            label: {
              color: '#ffffff'
            }
          }
        };
      });
    },

    build_base_map_option() {
      const region_style_map = {
        normal: {
          areaColor: {
            type: 'linear',
            x: 0.12,
            y: 0.08,
            x2: 0.88,
            y2: 0.92,
            colorStops: [
              { offset: 0, color: 'rgba(32, 92, 136, 0.98)' },
              { offset: 1, color: 'rgba(17, 59, 102, 0.98)' }
            ]
          },
          borderColor: 'rgba(122, 226, 245, 0.96)',
          borderWidth: 1.4,
          shadowBlur: 22,
          shadowColor: 'rgba(79, 194, 220, 0.22)'
        },
        emphasis: {
          areaColor: {
            type: 'linear',
            x: 0.12,
            y: 0.08,
            x2: 0.88,
            y2: 0.92,
            colorStops: [
              { offset: 0, color: 'rgba(44, 117, 166, 1)' },
              { offset: 1, color: 'rgba(25, 78, 129, 1)' }
            ]
          },
          borderColor: '#bffcff',
          borderWidth: 1.7,
          shadowBlur: 28,
          shadowColor: 'rgba(123, 240, 255, 0.28)'
        },
        selected: {
          areaColor: {
            type: 'linear',
            x: 0.12,
            y: 0.08,
            x2: 0.88,
            y2: 0.92,
            colorStops: [
              { offset: 0, color: 'rgba(58, 138, 191, 1)' },
              { offset: 1, color: 'rgba(32, 93, 150, 1)' }
            ]
          },
          borderColor: '#e5ffff',
          borderWidth: 1.9,
          shadowBlur: 34,
          shadowColor: 'rgba(137, 245, 255, 0.34)'
        }
      };

      return {
        animationDuration: 700,
        animationDurationUpdate: 350,
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(5, 14, 22, 0.96)',
          borderColor: 'rgba(79, 194, 220, 0.22)',
          borderWidth: 1,
          textStyle: {
            color: '#e6edf4',
            fontSize: 12
          },
          formatter: (params) => this.build_base_tooltip_content(params.name)
        },
        geo: {
          map: DISTRICT_MAP_NAME,
          roam: true,
          zoom: this.geo_zoom,
          center: this.geo_center || undefined,
          scaleLimit: {
            min: 1,
            max: 7
          },
          layoutCenter: ['50%', '54%'],
          layoutSize: '86%',
          label: {
            show: true,
            color: '#eefbff',
            fontSize: 11,
            fontWeight: 'bold'
          },
          itemStyle: {
            areaColor: region_style_map.normal.areaColor,
            borderColor: region_style_map.normal.borderColor,
            borderWidth: region_style_map.normal.borderWidth,
            shadowBlur: region_style_map.normal.shadowBlur,
            shadowColor: region_style_map.normal.shadowColor
          },
          emphasis: {
            label: {
              show: true,
              color: '#ffffff'
            },
            itemStyle: {
              areaColor: region_style_map.emphasis.areaColor,
              borderColor: region_style_map.emphasis.borderColor,
              borderWidth: region_style_map.emphasis.borderWidth,
              shadowBlur: region_style_map.emphasis.shadowBlur,
              shadowColor: region_style_map.emphasis.shadowColor
            }
          },
          regions: this.build_geo_regions(region_style_map)
        },
        series: [
          {
            name: '监测点位',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            geoIndex: 0,
            zlevel: 3,
            showEffectOn: 'render',
            rippleEffect: {
              period: 6,
              scale: 4.8,
              brushType: 'stroke'
            },
            symbolSize: (value) => {
              const metric_value = Number(value[2]) || 0;
              return 8 + Math.round(metric_value / 20);
            },
            itemStyle: {
              color: (params) => {
                const level_color_map = {
                  1: '#5fd0a5',
                  2: '#d2a655',
                  3: '#d27b78'
                };

                return level_color_map[params.value[3]] || '#4fc2dc';
              },
              shadowBlur: 18,
              shadowColor: 'rgba(7, 18, 28, 0.42)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              borderWidth: 0.6
            },
            data: this.build_scatter_series_data()
          }
        ]
      };
    },

    build_thematic_map_option(display_mode) {
      const thematic_records = this.build_thematic_records(display_mode);
      const thematic_lookup = Object.fromEntries(
        thematic_records.map((record) => [record.name, record.thematic_value])
      );

      const is_farmland = display_mode === 'farmland';
      const monitor_year = is_farmland ? this.farmland_monitor_year : this.construction_monitor_year;
      const layer_config = this.get_layer_config(display_mode);
      const tooltip_title = layer_config?.label || '专题值';

      return {
        animationDuration: 700,
        animationDurationUpdate: 350,
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: is_farmland ? 'rgba(8, 18, 20, 0.96)' : 'rgba(24, 12, 8, 0.96)',
          borderColor: is_farmland ? 'rgba(190, 236, 126, 0.24)' : 'rgba(255, 182, 96, 0.24)',
          borderWidth: 1,
          textStyle: {
            color: is_farmland ? '#edf7ef' : '#fff1e6',
            fontSize: 12
          },
          formatter: (params) => this.build_thematic_tooltip_content(
            params.name,
            thematic_lookup[params.name],
            tooltip_title,
            monitor_year
          )
        },
        geo: {
          map: DISTRICT_MAP_NAME,
          roam: true,
          zoom: this.geo_zoom,
          center: this.geo_center || undefined,
          scaleLimit: {
            min: 1,
            max: 7
          },
          layoutCenter: ['50%', '54%'],
          layoutSize: '86%',
          label: {
            show: true,
            color: is_farmland ? '#f5fde9' : '#fff2e1',
            fontSize: 11,
            fontWeight: 'bold'
          },
          itemStyle: {
            areaColor: is_farmland ? 'rgba(74, 164, 92, 0.9)' : 'rgba(210, 98, 30, 0.9)',
            borderColor: is_farmland ? 'rgba(218, 248, 171, 0.95)' : 'rgba(255, 214, 143, 0.96)',
            borderWidth: 1.3
          },
          emphasis: {
            label: {
              show: true,
              color: '#ffffff'
            }
          },
          regions: this.build_thematic_regions(display_mode)
        },
        series: []
      };
    },

    build_map_option() {
      if (this.display_mode === 'farmland') {
        return this.build_thematic_map_option('farmland');
      }

      if (this.display_mode === 'ndvi') {
        return this.build_thematic_map_option('ndvi');
      }

      return this.build_base_map_option();
    },

    render_map() {
      if (!this.chart_instance) {
        return;
      }

      this.chart_instance.setOption(this.build_map_option(), true);
    },

    render_empty_state() {
      if (!this.chart_instance) {
        return;
      }

      this.chart_instance.setOption({
        animation: false,
        backgroundColor: 'transparent',
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: '地图资源加载失败',
              fill: '#8db4c8',
              font: '14px "Microsoft YaHei"',
              textAlign: 'center'
            }
          }
        ]
      }, true);
    },

    focus_region(region_name) {
      const region_record = this.find_region_record(region_name);
      if (!region_record) {
        return;
      }

      this.selected_region_name = region_record.name;
      this.geo_center = region_record.coord;
      this.geo_zoom = 1.8;
      this.render_map();
      this.$emit('region-click', region_record.name);
    },

    reset_map_view() {
      this.selected_region_name = DEFAULT_CITY_NAME;
      this.geo_center = null;
      this.geo_zoom = DEFAULT_MAP_ZOOM;
      this.render_map();
      this.$emit('region-click', null);
    },

    update_monitor_year(year_value) {
      if (this.display_mode === 'ndvi') {
        this.construction_monitor_year = year_value;
        return;
      }

      this.farmland_monitor_year = year_value;
    },

    bind_chart_events() {
      if (!this.chart_instance) {
        return;
      }

      this.chart_instance.off('click');
      this.chart_instance.on('click', (params) => {
        if (!params || !params.name) {
          return;
        }

        this.focus_region(params.name);
      });

      this.chart_instance.off('georoam');
      this.chart_instance.on('georoam', () => {
        const option = this.chart_instance?.getOption();
        const geo_option = option?.geo?.[0];

        if (!geo_option) {
          return;
        }

        this.geo_zoom = geo_option.zoom || this.geo_zoom;
        this.geo_center = Array.isArray(geo_option.center) ? geo_option.center : this.geo_center;
      });
    },

    handle_resize() {
      this.chart_instance?.resize();
    }
  }
};
</script>

<style scoped>
.jiangxi_map {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 44%, rgba(69, 146, 184, 0.14), transparent 50%),
    radial-gradient(circle at 50% 54%, rgba(79, 194, 220, 0.06), transparent 62%),
    linear-gradient(180deg, rgba(5, 12, 19, 0.6), rgba(4, 10, 16, 0.24));
}

.jiangxi_map::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.015), transparent 24%),
    radial-gradient(circle at 50% 46%, rgba(125, 233, 255, 0.05), transparent 56%);
}

.jiangxi_map__chart {
  position: absolute;
  inset: 0;
  filter: drop-shadow(0 0 12px rgba(79, 194, 220, 0.05));
}

.jiangxi_map__title {
  position: absolute;
  left: 12px;
  top: 10px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;
}

.jiangxi_map__title strong {
  color: #eaf8ff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.jiangxi_map__title span {
  width: fit-content;
  padding-bottom: 4px;
  color: rgba(197, 242, 255, 0.78);
  font-size: 10px;
  letter-spacing: 0.12em;
  border-bottom: 1px solid rgba(177, 237, 255, 0.38);
}

.jiangxi_map__year_switcher {
  position: absolute;
  left: 50%;
  top: 12px;
  z-index: 4;
  display: flex;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(10, 22, 18, 0.76);
  box-shadow: inset 0 0 0 1px rgba(185, 230, 145, 0.14);
  transform: translateX(-50%);
}

.jiangxi_map__year_switcher--construction {
  background: rgba(28, 16, 10, 0.78);
  box-shadow: inset 0 0 0 1px rgba(255, 187, 103, 0.14);
}

.jiangxi_map__year_button {
  min-width: 56px;
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: rgba(231, 243, 220, 0.82);
  font-size: 11px;
  cursor: pointer;
}

.jiangxi_map__year_button:hover {
  background: rgba(142, 190, 99, 0.14);
}

.jiangxi_map__year_button--active {
  background: rgba(186, 220, 98, 0.22);
  color: #f6fee8;
  box-shadow: inset 0 0 0 1px rgba(215, 236, 141, 0.18);
}

.jiangxi_map__year_button--construction:hover {
  background: rgba(255, 161, 72, 0.14);
}

.jiangxi_map__year_button--construction.jiangxi_map__year_button--active {
  background: rgba(255, 171, 78, 0.22);
  color: #fff3df;
  box-shadow: inset 0 0 0 1px rgba(255, 209, 142, 0.18);
}

.jiangxi_map__legend {
  position: absolute;
  top: 88px;
  right: 14px;
  z-index: 4;
  width: 116px;
  padding: 12px 12px 10px;
  border-radius: 12px;
  background: rgba(8, 18, 22, 0.82);
  box-shadow:
    inset 0 0 0 1px rgba(201, 242, 177, 0.1),
    0 10px 24px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
}

.jiangxi_map__legend--construction {
  background: rgba(22, 15, 10, 0.82);
  box-shadow:
    inset 0 0 0 1px rgba(255, 204, 138, 0.1),
    0 10px 24px rgba(0, 0, 0, 0.22);
}

.jiangxi_map__legend_header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.jiangxi_map__legend_header strong {
  color: #eef7fb;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.jiangxi_map__legend_header span {
  color: rgba(205, 219, 228, 0.74);
  font-size: 10px;
}

.jiangxi_map__legend_body {
  margin-top: 10px;
  display: grid;
  grid-template-columns: auto 12px;
  grid-template-rows: auto 88px auto;
  justify-content: end;
  align-items: center;
  column-gap: 10px;
  row-gap: 6px;
}

.jiangxi_map__legend_level {
  color: rgba(229, 239, 245, 0.84);
  font-size: 10px;
  justify-self: start;
}

.jiangxi_map__legend_bar {
  grid-column: 2;
  grid-row: 1 / span 3;
  width: 12px;
  height: 88px;
  border-radius: 999px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.14),
    0 0 18px rgba(0, 0, 0, 0.18);
}

.jiangxi_map__legend_scale {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: rgba(229, 239, 245, 0.84);
  font-size: 10px;
  font-family: "JetBrains Mono", monospace;
}

.jiangxi_map__hud {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 10px;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  pointer-events: none;
}

.jiangxi_map__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  min-width: 132px;
  border-radius: 8px;
  background: rgba(4, 10, 16, 0.6);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.jiangxi_map__summary span {
  color: rgba(195, 208, 220, 0.72);
  font-size: 10px;
  letter-spacing: 0.06em;
}

.jiangxi_map__summary strong {
  color: #edf4fa;
  font-size: 14px;
  font-weight: 500;
}

.jiangxi_map__action {
  pointer-events: auto;
  min-width: 88px;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: #e3edf4;
  font-size: 12px;
  cursor: pointer;
}

.jiangxi_map__action:hover {
  background: rgba(255, 255, 255, 0.1);
}

@media (max-width: 920px) {
  .jiangxi_map__year_switcher {
    top: 12px;
    flex-wrap: wrap;
    justify-content: center;
    width: calc(100% - 24px);
  }

  .jiangxi_map__legend {
    top: auto;
    right: 12px;
    bottom: 62px;
    width: 104px;
  }

  .jiangxi_map__hud {
    flex-direction: column;
    align-items: stretch;
  }

  .jiangxi_map__action {
    width: 100%;
  }
}
</style>
